/**
 * helpers.js - Funciones utilitarias del frontend
 * =================================================
 * Funciones puras reutilizables en toda la aplicación.
 */

/**
 * Formatea un precio en CLP (pesos chilenos).
 * @param {number} amount - Monto a formatear
 * @returns {string} Precio formateado (ej: "$25.000")
 */
export function formatPrice(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function formatServicePrice(service) {
  if (!Number.isFinite(service.price)) return 'Valor por confirmar';
  const price = formatPrice(service.price);
  return service.priceType === 'from' ? `Desde ${price}` : price;
}

export function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
}

export function formatInputDate(dateValue) {
  if (!dateValue) return '';
  const [year, month, day] = dateValue.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

export function getTodayInputValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const temporaryInput = document.createElement('textarea');
  temporaryInput.value = text;
  temporaryInput.setAttribute('readonly', '');
  temporaryInput.style.position = 'fixed';
  temporaryInput.style.opacity = '0';
  document.body.appendChild(temporaryInput);
  temporaryInput.select();
  document.execCommand('copy');
  temporaryInput.remove();
}

/**
 * Formatea una fecha ISO a formato legible en español.
 * @param {string} isoDate - Fecha en formato ISO
 * @returns {string} Fecha formateada
 */
export function formatDate(isoDate) {
  return new Date(isoDate).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Genera un enlace de WhatsApp pre-llenado.
 * @param {string} phone - Número con código país (ej: "56912345678")
 * @param {string} message - Mensaje pre-llenado
 * @returns {string} URL de WhatsApp
 */
export function getWhatsAppLink(phone, message = '') {
  const encodedMsg = encodeURIComponent(message);
  return `https://wa.me/${phone}?text=${encodedMsg}`;
}

/**
 * Debounce: retrasa la ejecución de una función.
 * @param {Function} fn - Función a ejecutar
 * @param {number} delay - Milisegundos de espera
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
